/**
 * Recursively extracts all errorMessage values from a nested API response.
 * Works with both campaign creation and edit response structures.
 */
export const extractAllApiErrorMessages = (error) => {
    const messages = [];
    const responseData = error.response?.data;
    if (!responseData) return messages;

    const walk = (obj) => {
        if (Array.isArray(obj)) {
            obj.forEach((item) => {
                if (item?.errorMessage) {
                    messages.push(item.errorMessage);
                }
            });
        } else if (obj && typeof obj === "object") {
            Object.values(obj).forEach(walk);
        }
    };
    walk(responseData);
    return messages;
};

/**
 * Extracts indexed or global error messages from the API response.
 *
 * @param {Object} errorObj - The error object returned from the API response.
 * @returns {CampaignErrorMessages} An object containing extracted error messages.
 */
export const extractErrorMessages = (error) => {
    const errors = {};

        const responseData = error.response?.data?.edit_campaign_settings || {};

        if (responseData.campaign_name?.errorMessage) {
            errors.campaignName = responseData.campaign_name.errorMessage;
        }

        if (Array.isArray(responseData.campaign_lang) && responseData.campaign_lang[0]?.errorMessage) {
            errors.campaignLang = responseData.campaign_lang[0].errorMessage;
        }

        if (Array.isArray(responseData.budget?.default_budget) && responseData.budget.default_budget[0]?.errorMessage) {
            errors.defaultBudget = responseData.budget.default_budget[0].errorMessage;
        }

        if (
            Array.isArray(responseData.bidding_strategy?.bidding_strategy_option) &&
            responseData.bidding_strategy.bidding_strategy_option[0]?.errorMessage
        ) {
            errors.biddingStrategy = responseData.bidding_strategy.bidding_strategy_option[0].errorMessage;
        }

        if (
            Array.isArray(responseData.bidding_strategy?.ad_location) &&
            responseData.bidding_strategy.ad_location[0]?.errorMessage
        ) {
            errors.adLocation = responseData.bidding_strategy.ad_location[0].errorMessage;
        }

        if (
        Array.isArray(responseData.bidding_strategy?.bidding_value) &&
        responseData.bidding_strategy.bidding_value[0]?.errorMessage
        ) {
        errors.biddingValue =
            responseData.bidding_strategy.bidding_value[0].errorMessage;
        }

        if (
        Array.isArray(responseData.bidding_strategy?.bidding_max_value) &&
        responseData.bidding_strategy.bidding_max_value[0]?.errorMessage
        ) {
        errors.biddingMaxValue =
            responseData.bidding_strategy.bidding_max_value[0].errorMessage;
        }
        
        return errors
};

export const extractKeywordsErrorMessages = (error) => {
    const errors = {};

        const responseData = error.response?.data?.edit_campaign_keywords || {};

        if (
            Array.isArray(responseData.keywords) &&
            responseData.keywords?.[0]?.errorMessage
        ) {
            errors.keywords = responseData.keywords?.[0].errorMessage;
        }

        if (
            Array.isArray(responseData.keywords_negativ) &&
            responseData.keywords_negativ?.[0]?.errorMessage
        ) {
            errors.keywords_negative = responseData.keywords_negativ?.[0].errorMessage;
        }

        return errors
};

export const extractAdsCreativeErrorMessages = (errorObj) => {
    const errors = {};

    /**
     * Extracts errors by matching indexed paths in the response.
     *
     * @param {string} section - The section key from the response.
     * @param {string} basePath - The base path pattern to match indices.
     * @returns {Object|string|null} - Indexed errors or a global error message.
     */
    const extractIndexedErrorsByPath = (section, basePath) => {
        const sectionErrors = {};
        const sectionData = errorObj.campaign_ads_creative?.[section];

        if (sectionData?.length) {
            for (const err of sectionData) {
                const match = err.path.match(new RegExp(`${basePath}\\[(\\d+)\\]`));

                if (match) {
                    const index = `index-${match[1]}`;
                    if (!sectionErrors[index]) {
                        sectionErrors[index] = err.errorMessage;
                    }
                } else if (!Object.keys(sectionErrors).length) {
                    return err.errorMessage;
                }
            }
        }
        return Object.keys(sectionErrors).length ? sectionErrors : null;
    };

    /**
     * Extracts the first error message from sections containing lists.
     *
     * @param {string} section - The section key from the response.
     * @returns {Object|null} - Extracted errors by index or `null`.
     */
    const extractFirstErrorsByIndex = (section) => {
        const sectionErrors = {};
        if (errorObj.campaign_ads_creative?.[section]) {
            for (const [index, errList] of Object.entries(errorObj.campaign_ads_creative[section])) {
                if (errList.length) {
                    sectionErrors[index] = errList[0].errorMessage;
                }
            }
        }
        return Object.keys(sectionErrors).length ? sectionErrors : null;
    };

    if (errorObj.campaign_ads_creative?.business_name?.length) {
        errors.businessName = errorObj.campaign_ads_creative.business_name[0].errorMessage;
    }

    if (errorObj.campaign_ads_creative?.final_url?.length) {
        errors.finalUrl = errorObj.campaign_ads_creative.final_url[0].errorMessage;
    }

    errors.headline = extractFirstErrorsByIndex("headline_list") || extractIndexedErrorsByPath("headline", "assetsHeadline");
    errors.description = extractFirstErrorsByIndex("description_list") || extractIndexedErrorsByPath("description", "assetsDescription");
    errors.longHeadline = extractFirstErrorsByIndex("long_headline_list") || extractIndexedErrorsByPath("long_headline", "assetsLongHeadline");
    errors.siteLinkExtension = errorObj.campaign_ads_creative?.sitelink_list
    errors.callout = extractFirstErrorsByIndex("callout_list") || extractIndexedErrorsByPath("callout","assetsSiteLink")
    errors.snippets =  extractFirstErrorsByIndex("snippet_list") || extractIndexedErrorsByPath("snippet","assetsSiteLink");

    // Image errors
    errors.landscapeImage = extractFirstErrorsByIndex("marketing_image_list") || extractIndexedErrorsByPath("marketing_image", "assetsMarketingImage");
    errors.squareImage = extractFirstErrorsByIndex("square_marketing_image_list") || extractIndexedErrorsByPath("square_marketing_image", "assetsSquareMarketingImage");
    errors.portraitImage = extractFirstErrorsByIndex("portrait_marketing_image_list") || extractIndexedErrorsByPath("portrait_marketing_image", "assetsPortraitMarketingImage");
    errors.landscapeLogoImage = extractFirstErrorsByIndex("landscape_logo_image_list") || extractIndexedErrorsByPath("landscape_logo_image", "assetsLandscapeLogoImage");

    if (errorObj.campaign_ads_creative?.logo_image?.length) {
        errors.logoImage = errorObj.campaign_ads_creative.logo_image[0].errorMessage;
    }

    return errors;
};